import React, { isValidElement } from 'react';
import { observer } from 'mobx-react-lite';
import { SidebarPageLayout } from '@insurancemenu/imenu-components-lib';
import { SidebarPageLayoutProps } from '@insurancemenu/imenu-components-lib/dist/types/layouts/SidebarPageLayout';
import { bpHistory } from 'global/bpHistory';
import { useNavigation } from 'global/hooks/useNavigation';
import { ROUTES_CONFIG_V2, RouteV2Id } from 'routes/routesConfigV2';
import { LayoutPageFooterProps, LayoutPageHeaderProps } from '../types';

export interface PageInFlowWithSidebarLayoutProps
  extends Omit<SidebarPageLayoutProps, 'breadcrumbs' | 'pageHeader' | 'pageFooter'> {
  domainParams: Record<string, string>;
  pageId: RouteV2Id;
  /**
   * Takes onBack from useNavigation by default.
   * If you want to override the default behavior, you can pass a custom pageFooter object.
   * If you want to hide the footer, you can pass null.
   */
  pageFooter: LayoutPageFooterProps | null;
  pageHeader?: LayoutPageHeaderProps;
}

/** Integrates the no sidebar page with the useNavigation hook to handle routing and navigation state. */
const PageInFlowWithSidebarLayout: React.FC<PageInFlowWithSidebarLayoutProps> = observer(
  ({ domainParams, pageId, pageFooter, pageHeader, ...rest }) => {
    const { breadcrumbs, onBack } = useNavigation({ domainParams });
    const pageConfig = ROUTES_CONFIG_V2[pageId];

    function buildPageFooter(): SidebarPageLayoutProps['pageFooter'] {
      if (isValidElement(pageFooter)) {
        return pageFooter;
      }
      if (pageFooter != null && typeof pageFooter === 'object') {
        const footerProps = pageFooter as { onBack?: () => void };
        return { ...footerProps, onBack: footerProps.onBack ?? onBack };
      }
      return { onBack };
    }

    return (
      <SidebarPageLayout
        breadcrumbs={{
          crumbs: breadcrumbs.map((crumb) => ({
            label: crumb.label,
            onClick: (): void => {
              bpHistory.push(crumb.url);
            },
          })),
        }}
        pageHeader={{
          title: pageConfig.title,
          subtitle: pageConfig.subTitle,
          onBackButtonClick: onBack,
          ...pageHeader,
        }}
        pageFooter={pageFooter != null ? buildPageFooter() : undefined}
        {...rest}
      />
    );
  },
);

export default PageInFlowWithSidebarLayout;
